import { useEffect, useMemo, useState } from "react";
import {
  Purchases,
  PurchasesPackage,
  PACKAGE_TYPE,
  PURCHASES_ERROR_CODE,
} from "@revenuecat/purchases-capacitor";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  CreditCardIcon,
  CloudOffIcon,
  InfoIcon,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Button } from "../ui/button";
import Spinner from "../Spinner";
import EmptyState from "../EmptyState";
import SelectableOption from "./SelectableOption";
import ActivateTrialButton, { TRIAL_PLAN_ID } from "./ActivateTrialButton";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import {
  useActiveSubscription,
  useSyncRevenueCatStatus,
} from "@/tanstack-hooks";
import { SubscriptionStatus, SyncResult } from "@/generated-api";
import { useGetAccountLocale } from "@/hooks/useGetAccountLocale";
import { formatSubscriptionDate } from "@/lib/utils";

/**
 * iOS-only plan selector. Visually identical to SelectRecurringPlan, but
 * sourced from RevenueCat (StoreKit) and purchased via In-App Purchase
 * to comply with Apple guideline 3.1.1.
 *
 * Prices come from StoreKit pre-localized (priceString), so we never
 * format currency ourselves on this surface.
 */
export default function SelectRecurringPlanIOS() {
  const { toast } = useToast();
  const t = useTranslation();
  const navigate = useNavigate();
  const syncRevenueCatMutation = useSyncRevenueCatStatus();
  const activeSubscriptionQuery = useActiveSubscription();
  const locale = useGetAccountLocale();

  const offeringsQuery = useQuery({
    queryKey: ["revenueCatOfferings"],
    queryFn: async () => {
      const result = await Purchases.getOfferings();
      return result.current;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Display weekly first, then monthly. Memoized so the array reference is
  // stable across renders — otherwise the default-select effect below would
  // re-fire on every render.
  const packages: PurchasesPackage[] = useMemo(() => {
    const available = offeringsQuery.data?.availablePackages ?? [];
    const weekly = available.filter(
      (p) => p.packageType === PACKAGE_TYPE.WEEKLY,
    );
    const monthly = available.filter(
      (p) => p.packageType === PACKAGE_TYPE.MONTHLY,
    );
    return [...weekly, ...monthly];
  }, [offeringsQuery.data]);

  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null,
  );
  const [isPurchasing, setIsPurchasing] = useState(false);
  const trialEligible = activeSubscriptionQuery.data?.trial_eligible ?? false;
  const isTrialSelected = selectedPackageId === TRIAL_PLAN_ID;

  // Default-select monthly when packages load (matches web behavior — the
  // web variant defaults to plans[1], which is the monthly plan). Falls
  // back to the first available package if monthly isn't in the offering.
  useEffect(() => {
    if (selectedPackageId !== null || packages.length === 0) return;
    const monthly = packages.find(
      (p) => p.packageType === PACKAGE_TYPE.MONTHLY,
    );
    setSelectedPackageId((monthly ?? packages[0]).identifier);
  }, [packages, selectedPackageId]);

  const selectedPackage = packages.find(
    (p) => p.identifier === selectedPackageId,
  );

  async function handlePurchase() {
    if (!selectedPackage || isPurchasing) return;

    setIsPurchasing(true);
    try {
      await Purchases.purchasePackage({ aPackage: selectedPackage });
      // Apple confirmed the purchase — redirect right away instead of
      // gating on our backend sync round-trip (which can be slow and would
      // leave the user stranded on the checkout screen). The settings page
      // picks up the param, runs the RevenueCat sync, and shows the outcome
      // toast — mirrors the Stripe return_url flow (stripe_session_id).
      navigate("/settings?iap_purchase=1");
    } catch (error: unknown) {
      const err = error as {
        userCancelled?: boolean;
        code?: number | string;
        readableErrorCode?: string;
        message?: string;
      };
      const isUserCancelled =
        err?.userCancelled === true ||
        err?.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR ||
        err?.code === String(PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) ||
        err?.readableErrorCode === "PURCHASE_CANCELLED_ERROR";

      if (isUserCancelled) {
        // User tapped Cancel / X on Apple's sheet. No toast — they decided.
        setIsPurchasing(false);
        return;
      }
      toast({
        title: t.PAYMENT_CONFIRMATION_DIALOG.LABELS.FAILURE_MESSAGE,
        description: t.PAYMENT_CONFIRMATION_DIALOG.LABELS.FAILURE_DESCRIPTION,
        variant: "destructive",
        className: "dark rounded-2xl",
      });
      setIsPurchasing(false);
    }
  }

  async function handleRestore() {
    try {
      const { customerInfo } = await Purchases.restorePurchases();
      const hasPremium = Boolean(customerInfo.entitlements.active["premium"]);
      syncRevenueCatMutation.mutate(null, {
        onSuccess: (data) => {
          if (data === SyncResult.ACTIVE_ENTITLEMENT) {
            toast({
              // @ts-expect-error Type 'Element' is not assignable to type
              title: (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  {t.PAYMENT_CONFIRMATION_DIALOG.LABELS.PURCHASES_RESTORED}
                </div>
              ),
              variant: "default",
              className: "dark rounded-2xl",
            });
          } else if (data === SyncResult.SYNC_FAILED) {
            toast({
              title:
                t.PAYMENT_CONFIRMATION_DIALOG.LABELS.FAILED_TO_CHECK_PURCHASE,
              description: t.SHARED.ERRORS.PERSISTENT,
              variant: "destructive",
              className: "dark rounded-2xl",
            });
          } else {
            toast({
              // @ts-expect-error Type 'Element' is not assignable to type
              title: (
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  {t.PAYMENT_CONFIRMATION_DIALOG.LABELS.NO_PREVIOUS_PURCHASE}
                </div>
              ),
              variant: "default",
              className: "dark rounded-2xl",
            });
          }
        },
        onError: () => {
          toast({
            title:
              t.PAYMENT_CONFIRMATION_DIALOG.LABELS.FAILED_TO_CHECK_PURCHASE,
            description: t.SHARED.ERRORS.PERSISTENT,
            variant: "destructive",
            className: "dark rounded-2xl",
          });
        },
        onSettled: () => {
          navigate("/settings");
        },
      });

      if (hasPremium) navigate("/settings");
    } catch (error) {
      console.error("[RevenueCat] restore failed:", error);
      toast({
        variant: "destructive",
        title: t.PAYMENT_CONFIRMATION_DIALOG.LABELS.FAILED_TO_CHECK_PURCHASE,
        description: t.SHARED.ERRORS.PERSISTENT,
        className: "dark",
      });
    }
  }

  // Reuse the same "you already have a one-time plan" notice the web flow shows.
  function ActiveOneTimePlanExists() {
    const sub = activeSubscriptionQuery.data;
    if (
      sub?.subscription_status === SubscriptionStatus.ACTIVE &&
      sub.details?.is_recurring === false
    ) {
      return (
        <div className="flex flex-row gap-3 items-center rounded-lg border border-blue-500/30 p-3 bg-blue-600/30 text-blue-500">
          <InfoIcon className="w-6 h-6 shrink-0" />
          <div className="text-sm text-left">
            {t.SELECT_PLAN_CARD.LABELS.INFO_HAVE_EXISTING_UNTIL}{" "}
            {formatSubscriptionDate(sub.details.period_end, locale.code)}
            {locale.code === "ru" ? " " : ". "}
            {t.SELECT_PLAN_CARD.LABELS.INFO_SWITCHING_IMMEDIATE}
          </div>
        </div>
      );
    }
    return null;
  }

  if (offeringsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-[50vh]">
        <Spinner />
      </div>
    );
  }

  if (offeringsQuery.error || !offeringsQuery.data) {
    return (
      <EmptyState
        icon={CloudOffIcon}
        title={t.SELECT_PLAN_CARD.ERRORS.LOADING}
        message={t.SHARED.ERRORS.PERSISTENT}
      />
    );
  }

  if (packages.length === 0) {
    return (
      <EmptyState
        icon={CloudOffIcon}
        title={t.SELECT_PLAN_CARD.ERRORS.NONE_AVAILABLE}
        message={t.SHARED.ERRORS.PERSISTENT}
      />
    );
  }

  function getPlanName(pkg: PurchasesPackage): string {
    if (pkg.packageType === PACKAGE_TYPE.WEEKLY) {
      return "Weekly";
    }
    if (pkg.packageType === PACKAGE_TYPE.MONTHLY) {
      return t.SELECT_PLAN_CARD.LABELS.MONTHLY;
    }
    return pkg.product.title || pkg.identifier;
  }

  function getPlanPriceAndDuration(pkg: PurchasesPackage): string {
    // priceString is pre-localized by StoreKit (e.g. "€44,99", "$39.99", "¥6,000").
    const period =
      pkg.packageType === PACKAGE_TYPE.WEEKLY
        ? t.SELECT_PLAN_CARD.LABELS.WEEK
        : pkg.packageType === PACKAGE_TYPE.MONTHLY
          ? t.SELECT_PLAN_CARD.LABELS.MONTH
          : "";
    return period
      ? `${pkg.product.priceString}/${period}`
      : pkg.product.priceString;
  }

  return (
    <div className="flex w-full flex-col items-center text-center gap-y-5">
      <ActiveOneTimePlanExists />
      <div className="w-full flex flex-col gap-y-2">
        {trialEligible && (
          <SelectableOption
            planName={t.SELECT_PLAN_CARD.LABELS.TRIAL_PLAN_NAME}
            planPrice={t.SELECT_PLAN_CARD.LABELS.TRIAL_FREE}
            isSelected={isTrialSelected}
            onClick={() => setSelectedPackageId(TRIAL_PLAN_ID)}
          />
        )}
        {packages.map((pkg) => (
          <SelectableOption
            key={pkg.identifier}
            planName={getPlanName(pkg)}
            planPrice={getPlanPriceAndDuration(pkg)}
            isSelected={selectedPackageId === pkg.identifier}
            onClick={() => setSelectedPackageId(pkg.identifier)}
          />
        ))}
      </div>
      <div className="text-sm text-zinc-400">
        {t.SELECT_PLAN_CARD.LABELS.RECURRING_PAYMENT}
      </div>
      <div className="w-full flex flex-col gap-y-2">
        {isTrialSelected ? (
          <ActivateTrialButton />
        ) : (
          <Button
            className="w-full rounded-full py-6"
            disabled={isPurchasing || !selectedPackage}
            onClick={() => void handlePurchase()}
          >
            <CreditCardIcon className="w-5 h-5 mr-2" />
            {t.SELECT_PLAN_CARD.LABELS.PAY}
          </Button>
        )}
        <div className="text-sm text-zinc-400 mt-2">
          {t.SELECT_PLAN_CARD.LABELS.AGREE_TO_TERMS_AND_PRIVACY}
        </div>
      </div>
      <div className="w-full flex flex-col items-center">
        <button
          type="button"
          onClick={() => void handleRestore()}
          className="text-sm text-zinc-400 hover:text-white underline"
        >
          {t.SELECT_PLAN_CARD.LABELS.RESTORE_PURCHASES}
        </button>
      </div>
    </div>
  );
}
